import { ReactElement, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { clampMonth, EARLIEST_SELECTABLE_DATE } from '../../../helpers/DateInputHelper';
import CustomDropdown from '../DropDowns/CustomDropdown';
import DateTextInput from './DateTextInput';
import './Selector.scss';

type DayPickerInputDropdownProps = {
  selectedDate: Date | undefined;
  setSelectedDate: (arg: Date | undefined) => void;
  label: string;
  showDropdown: boolean;
  setShowDropdown: (arg: boolean) => void;
  maxDate?: Date;
  minDate?: Date;
};

/*
  Renders a keyboard-editable date input connected to a date picker popup. The input and the
  calendar share one underlying date value; typing behavior is handled by DateTextInput.
*/
const DayPickerInputDropdown = (props: DayPickerInputDropdownProps): ReactElement => {
  const {
    selectedDate,
    setSelectedDate,
    label,
    showDropdown,
    setShowDropdown,
    maxDate,
    minDate,
  } = props;

  const earliestDate = minDate ?? EARLIEST_SELECTABLE_DATE;
  const latestDate = maxDate ?? new Date();

  const [month, setMonth] = useState(() =>
    clampMonth(selectedDate ?? new Date(), earliestDate, latestDate),
  );
  const [entryError, setEntryError] = useState<string>();

  return (
    <CustomDropdown
      setShowDropdown={setShowDropdown}
      showDropdown={showDropdown}
      target={
        <DateTextInput
          id={`${label.replace(/\s/g, '-').toLowerCase()}-filter`}
          maxDate={latestDate}
          minDate={earliestDate}
          onChange={(date) => {
            setSelectedDate(date);
            if (date) setMonth(date);
          }}
          onClick={() => setShowDropdown(true)}
          onEntryError={setEntryError}
          onFocus={() =>
            setMonth(clampMonth(selectedDate ?? new Date(), earliestDate, latestDate))
          }
          placeholder={label}
          value={selectedDate}
        />
      }
    >
      <div className="action-date-picker">
        <DayPicker
          footer={
            entryError ? (
              <span className="date-entry-error" role="alert">
                {entryError}
              </span>
            ) : undefined
          }
          fromDate={earliestDate}
          month={month}
          onDayClick={(day, { selected }) => {
            setEntryError(undefined);
            setSelectedDate(selected ? undefined : day);
          }}
          onMonthChange={setMonth}
          selected={selectedDate}
          toDate={latestDate}
        />
      </div>
    </CustomDropdown>
  );
};

export default DayPickerInputDropdown;
